// src/core/validation/validation.ts (or your current path)

export interface ValidationResult {
  valid: boolean;
  message: string;
}

/* Prevent HTML or Script Injection */
export const validateNoteDescription = (description: string): ValidationResult => {
  const clean = description.trim();

  // 1. Required
  if (!clean) {
    return { valid: false, message: "Note is required." };
  }

  // 2. Minimum length
  if (clean.length < 3) {
    return { valid: false, message: "Note must be at least 3 characters long." };
  }

  // 3. Maximum length
  if (clean.length > 1000) {
    return { valid: false, message: "Note cannot exceed 1000 characters." };
  }

  // 4. Prevent HTML/script tags
  if (/<\/?[^>]+>/g.test(clean)) {
    return { valid: false, message: "Note cannot contain HTML or script tags." };
  }

  // 5. Prevent excessive consecutive spaces
  if (/\s{2,}/.test(clean)) {
    return {
      valid: false,
      message: "Note cannot contain excessive consecutive spaces.",
    };
  }

  // 6. Prevent starting or ending with special characters
  if (/^[^A-Za-z0-9]/.test(clean) || /[^A-Za-z0-9]$/.test(clean)) {
    return {
      valid: false,
      message: "Note cannot start or end with special characters.",
    };
  }

  // 7. Not only numbers
  if (/^[0-9]+$/.test(clean)) {
    return { valid: false, message: "Note cannot be only numbers." };
  }

  // 9. Prevent repeated characters (more than 3)
  if (/(.)\1{3,}/.test(clean)) {
    return {
      valid: false,
      message: "Note contains too many repeated characters.",
    };
  }

  // 10. Allowed characters check (letters, numbers, punctuation, formatting)
  const allowedRegex = /^[A-Za-z0-9 @$&/_-]+$/;
  if (!allowedRegex.test(clean)) {
    return {
      valid: false,
      message: "Only letters, numbers, spaces, and @$&/_- are allowed",
    };
  }

  return { valid: true, message: "" };
};

//-------------------------------------------
// ToDo Specific Validators
//-------------------------------------------
export const validateTodoTitle = (title: string): ValidationResult => {
  const t = (title || "").trim();

  // 1. Required
  if (!t) return { valid: false, message: "Title is required" };

  // 2. Minimum length
  if (t.length < 3) return { valid: false, message: "Title must be at least 3 characters" };

  // 3. Maximum length
  if (t.length > 100) return { valid: false, message: "Title too long (max 100)" };

  // 4. Prevent starting/ending with special characters
  if (/^[^A-Za-z0-9]/.test(t) || /[^A-Za-z0-9]$/.test(t)) {
    return { valid: false, message: "Title cannot start or end with special characters" };
  }

  // 5. Not only numbers
  if (/^[0-9]+$/.test(t)) {
    return { valid: false, message: "Title cannot be only numbers" };
  }


  // 7. Prevent repeated characters (more than 3)
  if (/(.)\1{3,}/.test(t)) {
    return { valid: false, message: "Title contains too many repeated characters" };
  }

  if (/\s{2,}/.test(t)) {
    return {
      valid: false,
      message: "Title cannot contain excessive consecutive spaces.",
    };
  }

  // 8. Allowed characters
  const allowedRegex = /^[A-Za-z0-9 @$&/_-]+$/;
  if (!allowedRegex.test(t)) {
    return { valid: false, message: "Only letters, numbers, spaces, and @$&/_- are allowed" };
  }

  return { valid: true, message: "" };
};

// --- Todo Description Validation ---
export const validateDescription = (desc: string): ValidationResult => {
  const d = (desc || "").trim();

  // Optional field
  if (!d) return { valid: true, message: "" };

  if (d.length > 150) {
    return { valid: false, message: "Description too long (max 150)" };
  }

  // Prevent excessive spaces
  if (/\s{3,}/.test(d)) {
    return {
      valid: false,
      message: "Description cannot contain excessive consecutive spaces.",
    };
  }

  // Prevent repeated characters
  if (/(.)\1{3,}/.test(d)) {
    return { valid: false, message: "Description contains too many repeated characters" };
  }

  // Prevent only numbers
  if (/^[0-9]+$/.test(d)) {
    return { valid: false, message: "Description cannot be only numbers" };
  }

  // Allowed characters
  const allowedRegex = /^[A-Za-z0-9 @$&/_-]+$/;
  if (!allowedRegex.test(d)) {
    return {
      valid: false,
      message: "Only letters, numbers, spaces, and @$&/_- are allowed",
    };
  }

  // Prevent start/end with special characters
  if (/^[^A-Za-z0-9]/.test(d) || /[^A-Za-z0-9]$/.test(d)) {
    return {
      valid: false,
      message: "Description cannot start or end with special characters",
    };
  }

  return { valid: true, message: "" };
};


// --- Todo Due Date Validation ---
export const validateDueDate = (date: string): ValidationResult => {
  if (!date) return { valid: false, message: "Due date is required" };

  const selected = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (selected < today) return { valid: false, message: "Due date cannot be in the past" };

  return { valid: true, message: "" };
};

// --- Required Select Validation ---
export const validateRequiredSelect = (value: any): ValidationResult => {
  return value ? { valid: true, message: "" } : { valid: false, message: "This field is required" };
};

//-------------------------------------------
// Calendar Specific Validators
//-------------------------------------------
export interface CalendarFormValues {
  title: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  allDay: boolean;

  description: string;
  attendees: string[];      // user IDs
  location: string;
  visibilityType: "public" | "private_user" | "private_admin" | "restricted";
}


export interface CalendarFormErrors {
  title?: string;
  startDate?: string;
  endDate?: string;
  time?: string;

  description?: string;
  attendees?: string;
  location?: string;
}


export const validateCalendarForm = (
  values: CalendarFormValues
): CalendarFormErrors => {
  const errors: CalendarFormErrors = {};

  const cleanTitle = values.title.trim();
  const cleanDescription = values.description.trim();
  const cleanLocation = values.location.trim();

  // --- Title Validation ---
  if (!cleanTitle) {
    errors.title = "Event title is required";
  } else if (cleanTitle.length < 3) {
    errors.title = "Event title must be at least 3 characters";
  } else if (cleanTitle.length > 100) {
    errors.title = "Event title cannot exceed 100 characters";
  } else if (/^[^A-Za-z0-9]/.test(cleanTitle) || /[^A-Za-z0-9]$/.test(cleanTitle)) {
    errors.title = "Event title cannot start or end with special characters";
  } else if (/^[0-9]+$/.test(cleanTitle)) {
    errors.title = "Event title cannot be only numbers";
  } else if (/\s{2,}/.test(cleanTitle)) {
    errors.title = "Event title cannot contain excessive consecutive spaces";
  } else if (/(.)\1{3,}/.test(cleanTitle)) {
    errors.title = "Event title contains too many repeated characters";
  } else if (!/^[A-Za-z0-9 @$&/_-]+$/.test(cleanTitle)) {
    errors.title = "Only letters, numbers, spaces, and @$&/_- are allowed";
  }

  // --- Dates Validation ---
  if (!values.startDate) errors.startDate = "Start date is required";
  if (!values.endDate) errors.endDate = "End date is required";

  if (values.startDate && values.endDate) {
    if (new Date(values.endDate) < new Date(values.startDate)) {
      errors.endDate = "End date cannot be before start date";
    }
  }

  // --- Time Validation (only if not all-day) ---
  if (!values.allDay) {
    if (!values.startTime || !values.endTime) {
      errors.time = "Start and end time are required";
    } else if (
      values.startDate === values.endDate &&
      values.endTime <= values.startTime
    ) {
      errors.time = "End time must be after start time";
    }
  }

  // --- Description Validation (optional) ---
  if (cleanDescription) {
  if (cleanDescription.length > 60) {
    errors.description = "Description must be under 60 characters";
  } else if (/^[^A-Za-z0-9]/.test(cleanDescription) || /[^A-Za-z0-9]$/.test(cleanDescription)) {
    errors.description = "Description cannot start or end with special characters";
  } else if (/^[0-9]+$/.test(cleanDescription)) {
    errors.description = "Description cannot be only numbers";
  } else if (/\s{3,}/.test(cleanDescription)) {
    errors.description = "Description cannot contain excessive consecutive spaces";
  } else if (/(.)\1{3,}/.test(cleanDescription)) {
    errors.description = "Description contains too many repeated characters";
  } else if (!/^[A-Za-z0-9 @$&/_-]+$/.test(cleanDescription)) {
  errors.description = "Only letters, numbers, spaces, and @$&/_- are allowed";
}
}


  // --- Location Validation (optional) ---
  if (cleanLocation) {
    if (cleanLocation.length > 100) {
      errors.location = "Location must be under 100 characters";
    } else if (/^[^A-Za-z0-9]/.test(cleanLocation) || /[^A-Za-z0-9]$/.test(cleanLocation)) {
      errors.location = "Location cannot start or end with special characters";
    } else if (/^[0-9]+$/.test(cleanLocation)) {
      errors.location = "Location cannot be only numbers";
    } else if (/^[^A-Za-z0-9]+$/.test(cleanLocation)) {
      errors.location = "Location cannot contain only symbols";
    } else if (/(.)\1{3,}/.test(cleanLocation)) {
      errors.location = "Location contains too many repeated characters";
    } else if (!/^[A-Za-z0-9 @$&/_-]+$/.test(cleanLocation)) {
      errors.location = "Location contains disallowed characters";
    }
  }

  // --- Attendees Validation (if restricted visibility) ---
  if (values.visibilityType === "restricted" && values.attendees.length === 0) {
    errors.attendees = "Please add at least one attendee";
  }

  return errors;
};


